import type { CSSProperties } from 'react';
import {
  NOTIFICATION_MARKDOWN_CSS,
  clampNotificationMarkdownToVisibleLimit,
  renderNotificationMarkdown,
  toNotificationGlimpse,
} from '@playcastdotio/SharedComponents';
import { formatNotificationAge } from './utils/formatNotificationAge';

export interface NotificationListItem {
  id: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

const rowButtonStyle: CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid rgba(228,228,228,0.12)',
  background: 'rgba(228,228,228,0.067)',
  cursor: 'pointer',
  textAlign: 'left',
};

const titleStyle: CSSProperties = {
  margin: 0,
  color: 'rgba(228,228,228,0.92)',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 590,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  minWidth: 0,
};

const timestampStyle: CSSProperties = {
  margin: '0 6px 0 0',
  color: 'rgba(228,228,228,0.37)',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 400,
  flexShrink: 0,
};

export function NotificationRow({
  item,
  expanded,
  showUnreadDot,
  onClick,
}: {
  item: NotificationListItem;
  expanded: boolean;
  showUnreadDot: boolean;
  onClick: () => void;
}) {
  const body = clampNotificationMarkdownToVisibleLimit(item.body);
  const collapsed = toNotificationGlimpse(body);

  const bodyStyle: CSSProperties = expanded
    ? {
        margin: 0,
        color: 'rgba(228,228,228,0.92)',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      }
    : {
        margin: 0,
        color: 'rgba(228,228,228,0.37)',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      };

  return (
    <button type='button' onClick={onClick} style={rowButtonStyle}>
      {expanded && <style>{NOTIFICATION_MARKDOWN_CSS}</style>}
      <div
        style={{
          display: 'flex',
          flexFlow: 'row',
          alignItems: 'flex-start',
          justifyContent: 'start',
          gap: 8,
          width: '100%',
        }}
      >
        <div
          style={{
            width: 8,
            height: 16,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: showUnreadDot ? '#ED1C24' : 'transparent',
              boxShadow: showUnreadDot ? '0 0 0 1px rgba(237, 28, 36, 0.35)' : undefined,
            }}
            aria-hidden={!showUnreadDot}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: expanded ? 4 : 2, width: '100%', flex: '1 1 0%', minWidth: 0 }}>
          <p style={titleStyle} data-testid='notification-row-title'>
            {item.title}
          </p>
          {expanded && (
            <div
              style={{
                width: '100%',
                height: 1,
                background: 'rgba(228,228,228,0.3)',
                flexShrink: 0,
              }}
              aria-hidden='true'
            />
          )}
          {expanded ? (
            <div
              className='notification-md'
              style={bodyStyle}
              data-testid='notification-row-body'
              dangerouslySetInnerHTML={{ __html: renderNotificationMarkdown(body) }}
            />
          ) : (
            <p style={bodyStyle} data-testid='notification-row-glimpse'>
              {collapsed}
            </p>
          )}
        </div>
        <p style={{ ...timestampStyle, lineHeight: '16px', height: 16, display: 'flex', alignItems: 'center' }}>
          {formatNotificationAge(item.createdAt)}
        </p>
      </div>
    </button>
  );
}
